import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { CheckCircle2, ClipboardCopy, AlertTriangle, Clock } from 'lucide-react';
import { ToolHeader } from '../components/ToolHeader';
import {
  applyReplace,
  testRegex,
  type RegexMatch,
  type RegexResult,
} from '../lib/regexTester';

const FLAG_OPTIONS: ReadonlyArray<{ id: string; label: string; tip: string }> = [
  { id: 'g', label: 'g', tip: 'global — 全部匹配' },
  { id: 'i', label: 'i', tip: 'ignoreCase — 大小写不敏感' },
  { id: 'm', label: 'm', tip: 'multiline — ^ / $ 匹配行首行尾' },
  { id: 's', label: 's', tip: 'dotall — . 匹配换行' },
  { id: 'u', label: 'u', tip: 'unicode — 启用 Unicode 转义与码点' },
  { id: 'y', label: 'y', tip: 'sticky — 仅从 lastIndex 开始匹配' },
];

const SAMPLE_TEST_STRING = `mail me at alice@example.com or bob@test.org
phone: 13800138000
year: 2024-08-15
visit https://example.com/path?x=1`;

/**
 * `/tools/validate/regex` — Regex Tester.
 *
 * The matcher runs in a Web Worker via `testRegex`; the page
 * debounces edits at 200ms and re-issues a fresh request on every
 * change. Replacement is synchronous (it runs on the main thread,
 * because by the time we'd replace we know the pattern doesn't
 * catastrophically backtrack).
 */
export default function RegexTool(): JSX.Element {
  const [pattern, setPattern] = useState<string>('(\\w+)@(\\w+\\.\\w+)');
  const [flags, setFlags] = useState<string>('g');
  const [testString, setTestString] = useState<string>(SAMPLE_TEST_STRING);
  const [result, setResult] = useState<RegexResult | null>(null);
  const [showReplace, setShowReplace] = useState(false);
  const [replacement, setReplacement] = useState<string>('$1[at]$2');

  // Debounce — also gates the worker round-trip so quick keystrokes
  // don't pile up.
  useEffect(() => {
    if (pattern.length === 0) {
      setResult(null);
      return;
    }
    const t = window.setTimeout(() => {
      void (async () => {
        const r = await testRegex({ pattern, flags, testString });
        setResult(r);
      })();
    }, 200);
    return () => window.clearTimeout(t);
  }, [pattern, flags, testString]);

  const replaceResult = useMemo(() => {
    if (!showReplace || pattern.length === 0) return null;
    return applyReplace({ pattern, flags, testString }, replacement);
  }, [showReplace, pattern, flags, testString, replacement]);

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 p-4">
      <ToolHeader
        toolId="regex"
        category="validate"
        description="Web Worker 跑 RegExp.exec，200ms 超时切断 catastrophic backtracking；6 个 flag 可选 + 替换面板可折叠。"
      />

      {/* Pattern + flags */}
      <section className="card-pad flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-ink-dim">/</span>
          <input
            type="text"
            value={pattern}
            onChange={(e) => setPattern(e.target.value)}
            className="input min-w-[280px] flex-1 py-1 font-mono text-[12.5px]"
            placeholder="正则表达式 — 例如 (\\w+)@(\\w+)"
            spellCheck={false}
          />
          <span className="font-mono text-ink-dim">/</span>
          <FlagChips value={flags} onChange={setFlags} />
        </div>
        <ResultStatus result={result} />
      </section>

      {/* Test string + match list (split layout) */}
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 lg:grid-cols-[3fr_2fr] lg:grid-rows-1">
        <section className="card-pad flex min-h-0 flex-col gap-1">
          <header className="flex items-center justify-between text-[11px] uppercase tracking-wide text-ink-dim">
            <span>测试串</span>
            <span>{testString.length.toLocaleString()} 字符</span>
          </header>
          <textarea
            value={testString}
            onChange={(e) => setTestString(e.target.value)}
            className="input min-h-[160px] flex-1 resize-none py-2 font-mono text-[12.5px]"
            spellCheck={false}
            placeholder="贴入待测试文本……"
          />
        </section>

        <section className="card-pad flex min-h-0 flex-col gap-2">
          <header className="text-[11px] uppercase tracking-wide text-ink-dim">
            匹配（{result && result.ok ? result.matches.length : 0}）
          </header>
          <MatchList result={result} />
        </section>
      </div>

      {/* Replace panel */}
      <section className="card-pad flex flex-col gap-2">
        <button
          type="button"
          onClick={() => setShowReplace((v) => !v)}
          className="self-start text-[12px] text-accent hover:underline"
        >
          {showReplace ? '收起替换' : '展开替换面板'}
        </button>
        {showReplace && (
          <>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-ink-muted">替换为</span>
              <input
                type="text"
                value={replacement}
                onChange={(e) => setReplacement(e.target.value)}
                className="input min-w-[280px] flex-1 py-1 font-mono text-[12.5px]"
                placeholder="支持 $1 / $2 / $<name> 反向引用"
                spellCheck={false}
              />
            </div>
            <ReplaceOutput out={replaceResult} />
          </>
        )}
      </section>
    </div>
  );
}

function FlagChips({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}): JSX.Element {
  const enabled = new Set(value.split(''));
  const toggle = (f: string): void => {
    const next = new Set(enabled);
    if (next.has(f)) next.delete(f);
    else next.add(f);
    onChange(Array.from(next).join(''));
  };
  return (
    <div className="flex items-center gap-1">
      {FLAG_OPTIONS.map((f) => (
        <button
          key={f.id}
          type="button"
          onClick={() => toggle(f.id)}
          className={clsx(
            'rounded-md border px-2 py-0.5 font-mono text-[12px] transition',
            enabled.has(f.id)
              ? 'border-accent bg-accent/10 text-accent'
              : 'border-line text-ink-muted hover:border-accent/60 hover:text-accent',
          )}
          title={f.tip}
        >
          {f.label}
        </button>
      ))}
    </div>
  );
}

function ResultStatus({ result }: { result: RegexResult | null }): JSX.Element | null {
  if (!result) return null;
  if (result.ok) {
    return (
      <p className="inline-flex items-center gap-1 text-[12px] text-emerald-600 dark:text-emerald-400">
        <CheckCircle2 className="h-3.5 w-3.5" />
        匹配 {result.matches.length} 处
      </p>
    );
  }
  if ('timeout' in result) {
    return (
      <p className="inline-flex items-center gap-1 text-[12px] text-amber-600 dark:text-amber-400">
        <Clock className="h-3.5 w-3.5" />
        超时 — 该正则可能存在灾难性回溯，已切断 Worker
      </p>
    );
  }
  return (
    <p className="inline-flex items-center gap-1 text-[12px] text-rose-600 dark:text-rose-400">
      <AlertTriangle className="h-3.5 w-3.5" />
      {result.error}
    </p>
  );
}

function MatchList({ result }: { result: RegexResult | null }): JSX.Element {
  if (!result || !result.ok) {
    return (
      <p className="text-[12px] text-ink-dim">
        {result && !result.ok && 'timeout' in result
          ? '— 已超时 —'
          : '— 编辑正则或测试串以查看匹配 —'}
      </p>
    );
  }
  if (result.matches.length === 0) {
    return <p className="text-[12px] text-ink-dim">— 无匹配 —</p>;
  }
  return (
    <ul className="flex min-h-0 flex-1 flex-col divide-y divide-line/40 overflow-auto">
      {result.matches.map((m, i) => (
        <MatchRow key={`${i}-${m.index}`} match={m} index={i} />
      ))}
    </ul>
  );
}

function MatchRow({
  match,
  index,
}: {
  match: RegexMatch;
  index: number;
}): JSX.Element {
  return (
    <li className="flex flex-col gap-1 py-1.5 font-mono text-[12px]">
      <div className="flex items-center gap-2">
        <span className="w-7 shrink-0 text-right text-ink-dim">{index + 1}.</span>
        <span className="min-w-0 flex-1 break-all text-ink">{match.fullText}</span>
        <span className="shrink-0 text-[10.5px] text-ink-dim">
          @{match.index}, len {match.length}
        </span>
        <CopyMicroButton value={match.fullText} />
      </div>
      {match.groups.length > 0 && (
        <div className="ml-9 flex flex-wrap items-center gap-2 text-[11.5px]">
          {match.groups.map((g, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-1 rounded bg-bg-soft px-1.5 py-0.5"
            >
              <span className="text-ink-dim">${i + 1}</span>
              <span className="text-ink">{g || '(空)'}</span>
            </span>
          ))}
        </div>
      )}
      {Object.keys(match.namedGroups).length > 0 && (
        <div className="ml-9 flex flex-wrap items-center gap-2 text-[11.5px]">
          {Object.entries(match.namedGroups).map(([k, v]) => (
            <span
              key={k}
              className="inline-flex items-center gap-1 rounded bg-accent/10 px-1.5 py-0.5"
            >
              <span className="text-ink-dim">&lt;{k}&gt;</span>
              <span className="text-ink">{v}</span>
            </span>
          ))}
        </div>
      )}
    </li>
  );
}

function ReplaceOutput({
  out,
}: {
  out: { ok: true; output: string } | { ok: false; error: string } | null;
}): JSX.Element | null {
  if (!out) return null;
  if (!out.ok) {
    return (
      <p className="text-[12px] text-rose-600 dark:text-rose-400">{out.error}</p>
    );
  }
  return (
    <pre className="max-h-[160px] overflow-auto rounded border border-line bg-bg-soft p-2 font-mono text-[12px] text-ink">
      {out.output}
    </pre>
  );
}

function CopyMicroButton({ value }: { value: string }): JSX.Element {
  const [done, setDone] = useState(false);
  const onClick = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      setDone(true);
      window.setTimeout(() => setDone(false), 900);
    } catch {
      /* ignore */
    }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className="shrink-0 rounded p-0.5 text-ink-dim hover:bg-bg-soft hover:text-accent"
      title="复制此匹配"
    >
      {done ? (
        <CheckCircle2 className="h-3 w-3 text-emerald-500" />
      ) : (
        <ClipboardCopy className="h-3 w-3" />
      )}
    </button>
  );
}
